import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Camera, CheckCircle, XCircle, AlertTriangle, Upload } from 'lucide-react';
import { usePhotoUpload } from '../../hooks/usePhotoUpload';
import PhotoUploadWidget from '../media/PhotoUploadWidget';

const PhotoTest = () => {
  const { user } = useAuth();
  const [testResult, setTestResult] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testError, setTestError] = useState<string>('');
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);

  const handlePhotoUploadComplete = (urls: string[]) => {
    setUploadedUrls(urls);
    setTestResult('success');
    console.log('Photos uploaded successfully:', urls);
  };

  const handlePhotoUploadError = (error: string) => {
    setTestError(error);
    setTestResult('error');
    console.error('Photo upload error:', error);
  };

  const resetTest = () => {
    setTestResult('idle');
    setTestError('');
    setUploadedUrls([]);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Prueba de Subida de Fotos
      </h3>
      
      <div className="space-y-6">
        {/* Upload Widget */}
        <PhotoUploadWidget
          onUploadComplete={handlePhotoUploadComplete}
          onUploadError={handlePhotoUploadError}
          maxPhotos={3}
          showPreview={true}
          className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
        />

        {/* Test Results */}
        {testResult === 'success' && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-green-800 dark:text-green-200">
                  ✅ Fotos subidas exitosamente
                </h4>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  Se subieron {uploadedUrls.length} fotos correctamente.
                </p>
                {uploadedUrls.length > 0 && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {uploadedUrls.slice(0, 4).map((url, index) => (
                      <img
                        key={index}
                        src={url}
                        alt={`Uploaded ${index + 1}`}
                        className="w-full h-20 object-cover rounded"
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {testResult === 'error' && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center">
              <XCircle className="h-5 w-5 text-red-500 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                  ❌ Error en la subida de fotos
                </h4>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  {testError}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Reset Button */}
        {testResult !== 'idle' && (
          <button
            onClick={resetTest}
            className="w-full px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Reiniciar Prueba
          </button>
        )}

        {/* Instructions */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                Información de la Prueba
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 mt-1 space-y-1">
                <li>• Esta prueba verifica que las fotos se puedan subir correctamente</li>
                <li>• Se valida el tamaño, formato y cantidad de archivos</li>
                <li>• Las fotos se comprimen automáticamente para optimizar el almacenamiento</li>
                <li>• En modo demo, se crean URLs temporales para las fotos</li>
                <li>• En producción, las fotos se subirían a Supabase Storage</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Current User Info */}
        {user && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
              Usuario Actual
            </h4>
            <div className="flex items-center space-x-3">
              <img 
                src={user.avatar_url || ''} 
                alt={user.name} 
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Rol: {user.role}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoTest;